import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import investorPortalApi from "@/lib/investorPortalApi";

const Section = ({ title, eyebrow, accent = "sage", children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.05 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
    data-testid={testId}
  >
    <div className={`absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl ${
      accent === "sage" ? "bg-sageMint" : accent === "sky" ? "bg-skyMist" : "bg-butterCream"
    }`} aria-hidden />
    {eyebrow && <p className="wit-eyebrow text-raspberry mb-2">{eyebrow}</p>}
    <h2 className="font-serif text-xl text-cocoa mb-5">{title}</h2>
    {children}
  </motion.section>
);

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, type = "text", placeholder, testId }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa transition-colors"
    data-testid={testId} />
);

const MultiSelect = ({ value, onChange, options, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((opt) => {
      const sel = (value || []).includes(opt);
      return (
        <button key={opt} type="button"
          onClick={() => onChange(sel ? (value || []).filter((v) => v !== opt) : [...(value || []), opt])}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            sel ? "bg-raspberry text-white border-raspberry" : "bg-porcelain text-cocoa border-sandBeige hover:border-raspberry"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const INVESTOR_TYPES = ["Angel", "VC", "Family Office", "Corporate", "Accelerator", "Government", "Fund of Funds"];
const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B", "Growth", "Late Stage"];
const SECTORS = ["SaaS", "FinTech", "HealthTech", "EdTech", "CleanTech", "DeepTech", "AI/ML", "E-Commerce", "Mobility", "FoodTech", "PropTech", "InsurTech", "LegalTech", "HRTech", "Cybersecurity", "Biotech", "Gaming", "Media", "Social Impact"];
const BIZ_MODELS = ["B2B", "B2C", "B2B2C", "Marketplace", "Platform", "Hardware", "Services"];

export default function InvestorThesis() {
  const { t } = useTranslation();
  const [thesis, setThesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    investorPortalApi.get("/thesis")
      .then(({ data }) => setThesis(data))
      .finally(() => setLoading(false));
  }, []);

  const upd = (key, val) => setThesis((p) => ({ ...p, [key]: val }));

  const save = async (section, fields) => {
    setSaving(section);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f] = thesis?.[f]; });
      await investorPortalApi.patch("/thesis", payload);
      toast.success(t("invPortal.thesis.saved"));
    } catch (_e) {
      toast.error(t("invPortal.thesis.saveFailed"));
    } finally {
      setSaving(null);
    }
  };

  const renderSaveBar = (section, fields) => (
    <div className="flex items-center justify-end gap-2 mt-5 pt-5 border-t border-sandBeige">
      <button type="button" onClick={() => save(section, fields)} disabled={saving === section}
        className="bg-raspberry hover:bg-coral disabled:opacity-60 text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
        data-testid={`inv-save-thesis-${section}`}
      >
        {saving === section ? <><Loader2 size={14} className="animate-spin" /> {t("invPortal.thesis.saving")}</> : <><Check size={14} /> {t("invPortal.thesis.save")}</>}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto rounded-3xl border border-sandBeige bg-white p-8 flex items-center justify-center text-warmGrey gap-2">
        <Loader2 className="animate-spin" size={18} /> {t("invPortal.thesis.loading")}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="investor-portal-thesis">
      <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.thesis.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">{t("invPortal.thesis.title")}</h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.thesis.lead")}</p>
      </motion.header>

      {/* Investor Type & Stage */}
      <Section title={t("invPortal.thesis.typeStageSectionTitle")} eyebrow={t("invPortal.thesis.typeStageEyebrow")} accent="sage" testId="inv-thesis-type-stage">
        <div className="flex flex-col gap-5">
          <Field label={t("invPortal.fields.investor_types")}>
            <MultiSelect value={thesis?.investor_types} onChange={(v) => upd("investor_types", v)} options={INVESTOR_TYPES} testId="inv-thesis-types" />
          </Field>
          <Field label={t("invPortal.fields.stage_focus")}>
            <MultiSelect value={thesis?.stage_focus} onChange={(v) => upd("stage_focus", v)} options={STAGES} testId="inv-thesis-stages" />
          </Field>
        </div>
        {renderSaveBar("type_stage", ["investor_types", "stage_focus"])}
      </Section>

      {/* Ticket */}
      <Section title={t("invPortal.thesis.ticketSectionTitle")} eyebrow={t("invPortal.thesis.ticketEyebrow")} accent="sky" testId="inv-thesis-ticket">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t("invPortal.fields.ticket_min")}>
            <TextInput value={thesis?.ticket_min} onChange={(v) => upd("ticket_min", v ? parseInt(v, 10) || null : null)} type="number" placeholder="25000" testId="inv-thesis-ticket-min" />
          </Field>
          <Field label={t("invPortal.fields.ticket_max")}>
            <TextInput value={thesis?.ticket_max} onChange={(v) => upd("ticket_max", v ? parseInt(v, 10) || null : null)} type="number" placeholder="500000" testId="inv-thesis-ticket-max" />
          </Field>
          <Field label={t("invPortal.fields.currency")}>
            <select value={thesis?.currency || "EUR"} onChange={(e) => upd("currency", e.target.value)}
              className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa"
              data-testid="inv-thesis-currency"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="CHF">CHF</option>
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={thesis?.follow_on_possible || false} onChange={(e) => upd("follow_on_possible", e.target.checked)}
              className="w-5 h-5 rounded border-sandBeige text-raspberry focus:ring-raspberry" />
            <span className="text-sm text-cocoa">{t("invPortal.fields.follow_on")}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={thesis?.is_lead || false} onChange={(e) => upd("is_lead", e.target.checked)}
              className="w-5 h-5 rounded border-sandBeige text-raspberry focus:ring-raspberry" />
            <span className="text-sm text-cocoa">{t("invPortal.fields.is_lead")}</span>
          </label>
        </div>
        {renderSaveBar("ticket", ["ticket_min", "ticket_max", "currency", "follow_on_possible", "is_lead", "is_follow", "syndicate_possible", "co_invest_possible"])}
      </Section>

      {/* Sectors & Geography */}
      <Section title={t("invPortal.thesis.sectorsSectionTitle")} eyebrow={t("invPortal.thesis.sectorsEyebrow")} accent="sage" testId="inv-thesis-sectors">
        <div className="flex flex-col gap-5">
          <Field label={t("invPortal.fields.sectors_preferred")}>
            <MultiSelect value={thesis?.sectors_preferred} onChange={(v) => upd("sectors_preferred", v)} options={SECTORS} testId="inv-thesis-sectors-pref" />
          </Field>
          <Field label={t("invPortal.fields.regions_focus")}>
            <TextInput value={(thesis?.regions_focus || []).join(", ")} onChange={(v) => upd("regions_focus", v.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="DACH, EU, Global" testId="inv-thesis-regions" />
          </Field>
          <Field label={t("invPortal.fields.business_models")}>
            <MultiSelect value={thesis?.business_models} onChange={(v) => upd("business_models", v)} options={BIZ_MODELS} testId="inv-thesis-biz-models" />
          </Field>
        </div>
        {renderSaveBar("sectors", ["sectors_preferred", "sectors_open", "sectors_excluded", "regions_focus", "business_models"])}
      </Section>

      {/* Value Add & Thesis Text */}
      <Section title={t("invPortal.thesis.textSectionTitle")} eyebrow={t("invPortal.thesis.textEyebrow")} accent="butter" testId="inv-thesis-text">
        <div className="flex flex-col gap-5">
          <Field label={t("invPortal.fields.value_add")}>
            <TextInput value={(thesis?.value_add || []).join(", ")} onChange={(v) => upd("value_add", v.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Network, Strategy, Recruiting, …" testId="inv-thesis-value-add" />
          </Field>
          <Field label={t("invPortal.fields.thesis_text")}>
            <textarea value={thesis?.thesis_text || ""} onChange={(e) => upd("thesis_text", e.target.value)} rows={6}
              placeholder={t("invPortal.thesis.textPlaceholder")}
              className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa resize-none transition-colors"
              data-testid="inv-thesis-text-input" />
            <p className="text-xs text-warmGrey mt-1">{t("invPortal.thesis.textHint")}</p>
          </Field>
        </div>
        {renderSaveBar("text", ["value_add", "thesis_text", "traction_requirements", "diversity_interests"])}
      </Section>
    </div>
  );
}
